import DatoAnimation from '~/cms/components/DatoVideo'
import {type DefaultCinematicOverlayVideoFragment} from '../../_generated/fragments.graphql'
import {type AspectRatio} from '~/design-system/hg/components'
import {tv, type VariantProps} from 'tailwind-variants'
import {CINEMATIC_HERO_CONTAINER_ID} from '~/design-system/hg/modules/HgHero/_components/CinematicHero/constants'
import {type Theme} from '~/design-system/hg/tokens/colors'
import {cn} from '~/design-system/utils'

type CinematicResponsiveVideoProps = {
  desktopVideo?: DefaultCinematicOverlayVideoFragment['desktopVideo'] & {
    aspectRatio: AspectRatio
  }
  tabletVideo?: DefaultCinematicOverlayVideoFragment['tabletVideo'] & {
    aspectRatio: AspectRatio
  }
  mobileVideo?: DefaultCinematicOverlayVideoFragment['mobileVideo'] & {
    aspectRatio: AspectRatio
  }
  variant: VariantProps<typeof responsiveVideoVariants>['variant']
  loop?: boolean
}

type VideoConfig = {
  type: 'desktop' | 'tablet' | 'mobile'
  video: NonNullable<CinematicResponsiveVideoProps['desktopVideo']>
  aspectRatio: AspectRatio
}

/**
 * Responsive video container variants that adapt based on available video types.
 * Uses a generic approach where responsive classes are applied dynamically.
 */
const responsiveVideoVariants = tv({
  slots: {
    container: 'grid h-full',
    // Button containers use invisible/visible instead of hidden/grid to maintain video progress CSS animation.
    // The animation resets if toggling the display property.
    buttonContainer: 'visible z-[3]',
  },
  variants: {
    visibility: {
      // Desktop video visibility patterns
      'solo-asset': {
        container: '',
        buttonContainer: '',
      },
      'desktop-with-tablet': {
        container: 'hidden xl:grid',
        buttonContainer: 'invisible xl:visible',
      },
      'desktop-with-mobile': {
        container: 'hidden sm:grid',
        buttonContainer: 'invisible md:visible',
      },
      'desktop-with-both': {
        container: 'hidden xl:grid',
        buttonContainer: 'invisible xl:visible',
      },
      // Tablet video visibility patterns
      'tablet-with-desktop': {
        container: 'xl:hidden',
        buttonContainer: 'xl:invisible',
      },
      'tablet-with-both': {
        container: 'hidden sm:grid xl:hidden',
        buttonContainer: 'invisible sm:visible xl:invisible',
      },
      // Mobile video visibility patterns
      'mobile-with-desktop': {
        container: 'sm:hidden',
        buttonContainer: 'sm:invisible',
      },
      'mobile-with-both': {
        container: 'sm:hidden',
        buttonContainer: 'sm:invisible',
      },
    },
    variant: {
      stacked: {
        buttonContainer: 'row-start-2',
      },
      overlay: '',
    },
  },
})

/**
 * Determines the appropriate visibility variant for each video type based on available videos
 */
function getVideoVisibility(
  type: VideoConfig['type'],
  hasDesktop: boolean,
  hasTablet: boolean,
  hasMobile: boolean
): keyof typeof responsiveVideoVariants.variants.visibility {
  switch (type) {
    case 'desktop':
      if (hasTablet && hasMobile) return 'desktop-with-both'
      if (hasTablet) return 'desktop-with-tablet'
      if (hasMobile) return 'desktop-with-mobile'
      return 'solo-asset'

    case 'tablet':
      if (hasDesktop && hasMobile) return 'tablet-with-both'
      if (hasDesktop) return 'tablet-with-desktop'
      return 'solo-asset' // Fallback for tablet-only

    case 'mobile':
      if (hasDesktop && hasTablet) return 'mobile-with-both'
      if (hasDesktop) return 'mobile-with-desktop'
      return 'solo-asset' // Fallback for mobile-only

    default:
      return 'solo-asset'
  }
}

export default function CinematicResponsiveVideo({
  desktopVideo,
  tabletVideo,
  mobileVideo,
  variant,
  loop,
}: CinematicResponsiveVideoProps) {
  const videoConfigs: VideoConfig[] = []

  if (desktopVideo) {
    videoConfigs.push({
      type: 'desktop',
      video: desktopVideo,
      aspectRatio: desktopVideo.aspectRatio,
    })
  }

  if (tabletVideo) {
    videoConfigs.push({
      type: 'tablet',
      video: tabletVideo,
      aspectRatio: tabletVideo.aspectRatio,
    })
  }

  if (mobileVideo) {
    videoConfigs.push({
      type: 'mobile',
      video: mobileVideo,
      aspectRatio: mobileVideo.aspectRatio,
    })
  }

  const hasDesktop = !!desktopVideo
  const hasTablet = !!tabletVideo
  const hasMobile = !!mobileVideo

  return (
    <>
      {videoConfigs.map(({type, video, aspectRatio}) => {
        const visibility = getVideoVisibility(type, hasDesktop, hasTablet, hasMobile)
        const {container, buttonContainer} = responsiveVideoVariants({
          visibility,
          variant,
        })

        return (
          <DatoAnimation
            key={type}
            buttonContainerId={CINEMATIC_HERO_CONTAINER_ID}
            data={video}
            aspectRatio={aspectRatio}
            classNames={{
              container: container(),
              buttonContainer: buttonContainer(),
              video: cn(variant === 'overlay' && 'h-screen'),
            }}
            controlsTheme={video.controlsTheme?.name as Theme | undefined}
            loop={loop}
          />
        )
      })}
    </>
  )
}
